<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useNotification } from '@kyvg/vue3-notification'
import _ from 'lodash'
import SiteAdminService, { isPaywallSiteUnconfigured } from '@/services/SiteAdminService'
import { useSiteAdmin } from '@/composables/useSiteAdmin'
import { TX_STATUSES, TX_STATUS_LABELS, TX_STATUS_VARIANTS, RANGE_PRESETS, presetRange, isoDay, formatMoney, isRefundable, isIncomplete, unrefundableReason, refundTarget, paymentAmount, refLabel, productTitle, promoCodeOf, paidPurchaseCount, statusParam } from '@/Utils/siteTransactions'
import { periodAdverb } from '@/Utils/sitePeriod'
import { saveBlob, slugForFile } from '@/Utils/downloadFile'
import Spinner from '@/components/ui/Spinner.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import ModalBox from '@/components/modals/ModalBox.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import FormSelect from '@/components/Atoms/FormSelect.vue'
import FormPagination from '@/components/Atoms/FormPagination.vue'
import StatusPill from '@/components/Atoms/StatusPill.vue'
import Alert from '@/components/Atoms/Alert.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconSearch from '@/components/icon/IconSearch.vue'
import SiteRevenueSummary from '@/components/Sites/SiteRevenueSummary.vue'
import SiteRevenueChart from '@/components/Sites/SiteRevenueChart.vue'
import SiteTransactionPanel from '@/components/Sites/SiteTransactionPanel.vue'

// Transactions & revenue of one site (#25, round 5): castr walks the paywall
// invoices of the site's products and serves a newest-first, filterable page
// plus a summary over the selected date range with a daily `series`
// (internal/sites-control-plane-api.md §6e). Refunds are full refunds of one
// payment — the paywall/Stripe call has no partial mode — and only offered on
// rows castr marks `refundable`.
const props = defineProps({
	siteId: {
		type: String,
		required: true,
	},
	site: {
		type: Object,
		required: true,
	},
})
const { notify } = useNotification()
const route = useRoute()
const router = useRouter()
const admin = useSiteAdmin(() => props.siteId)

const PER_PAGE = 25
const loading = ref(true)
const refreshing = ref(false)
const loadError = ref(null)
const rows = ref([])
const meta = ref({ total: 0, last_page: 1 })
const summary = ref(null)
const page = ref(1)
const q = ref('')
const status = ref('')

// --- date range ------------------------------------------------------------------
const rangePreset = ref(RANGE_PRESETS.some((p) => p.id === route.query.range) ? String(route.query.range) : 'all')
const customFrom = ref(typeof route.query.from === 'string' ? route.query.from : isoDay(Date.now() - 29 * 86_400_000))
const customTo = ref(typeof route.query.to === 'string' ? route.query.to : isoDay())
const customValid = computed(() => /^\d{4}-\d{2}-\d{2}$/.test(customFrom.value) && /^\d{4}-\d{2}-\d{2}$/.test(customTo.value) && customFrom.value <= customTo.value)
const range = computed(() => {
	if (rangePreset.value === 'custom') return customValid.value ? { from: customFrom.value, to: customTo.value } : { from: undefined, to: undefined }
	return presetRange(rangePreset.value)
})
const fmtDay = (d) => (d ? new Date(`${d}T00:00:00Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '')
const rangeLabel = computed(() => {
	const { from, to } = range.value
	if (!from && !to) return summary.value?.first_at ? `All time · since ${new Date(summary.value.first_at).toLocaleDateString()}` : 'All time'
	const preset = RANGE_PRESETS.find((p) => p.id === rangePreset.value)
	const span = `${fmtDay(from)} – ${fmtDay(to)}`
	return preset?.days ? `Last ${preset.days} days · ${span}` : span
})
const setPreset = (id) => {
	rangePreset.value = id
	if (id === 'custom' && !customValid.value) return
	page.value = 1
	load()
}
const applyCustom = () => {
	if (!customValid.value) return
	page.value = 1
	load()
}
watch([rangePreset, range], () => {
	const query = { ...route.query }
	delete query.range
	delete query.from
	delete query.to
	if (rangePreset.value !== 'all') query.range = rangePreset.value
	if (rangePreset.value === 'custom' && customValid.value) {
		query.from = customFrom.value
		query.to = customTo.value
	}
	if (!_.isEqual(query, route.query)) router.replace({ query })
})

const paywallUnlinked = computed(() => !props.site.billing?.paywallSiteId || isPaywallSiteUnconfigured(loadError.value))
const REASON_TEXT = {
	paywall_runtime_key_rejected: 'Castr could not authenticate with the Paywall service (its runtime key was rejected). This is a server configuration problem — transactions are unavailable until it is fixed. Please contact support.',
	paywall_site_unconfigured: 'This site is not linked to your Paywall account yet. Retry publish from the Overview tab.',
	partial_refund_unsupported: 'Only a full refund of the payment is possible — partial amounts are not supported yet.',
	nothing_to_refund: 'This transaction has nothing left to refund.',
}
const describeError = (err, fallback) => REASON_TEXT[err?.reason] || err?.message || fallback
const loadErrorText = computed(() => describeError(loadError.value, 'Could not load transactions.'))

const statusOptions = [{ value: '', text: 'All statuses' }, ...TX_STATUSES.map((s) => ({ value: s, text: s === 'pending' ? 'Incomplete' : TX_STATUS_LABELS[s] }))]

const filterParams = () => _.omitBy({ status: statusParam(status.value), q: q.value.trim() || undefined, from: range.value.from, to: range.value.to }, _.isUndefined)

const load = async ({ refresh = false } = {}) => {
	if (refresh) refreshing.value = true
	else loading.value = true
	try {
		const params = _.omitBy({ page: page.value, per_page: PER_PAGE, ...filterParams(), refresh: refresh ? 1 : undefined }, _.isUndefined)
		const res = await SiteAdminService.listTransactions(props.siteId, params)
		rows.value = res?.data || []
		meta.value = res?.meta || { total: rows.value.length, last_page: 1 }
		if (res?.summary) summary.value = res.summary
		loadError.value = null
	} catch (err) {
		loadError.value = err
		rows.value = []
	}
	loading.value = false
	refreshing.value = false
}
const debouncedSearch = _.debounce(() => { page.value = 1; load() }, 350)
watch(q, debouncedSearch)
watch(status, () => { page.value = 1; load() })
watch(page, () => load())

onMounted(async () => {
	await Promise.all([load(), admin.loadContent().catch(() => {}), admin.loadCategories().catch(() => {})])
	if (route.query.tx) {
		const found = rows.value.find((t) => String(t.public_id) === String(route.query.tx) || String(t.id) === String(route.query.tx))
		if (found) openDetail(found)
	}
})

// --- chart -----------------------------------------------------------------------
const series = computed(() => (Array.isArray(summary.value?.series) ? summary.value.series : null))
const chartCurrency = computed(() => summary.value?.currency || 'USD')

// --- CSV -------------------------------------------------------------------------
const exporting = ref(false)
const exportCsv = async () => {
	if (exporting.value) return
	exporting.value = true
	try {
		const blob = await SiteAdminService.downloadTransactionsCsv(props.siteId, filterParams())
		const day = range.value.to || isoDay()
		saveBlob(blob instanceof Blob ? blob : new Blob([blob], { type: 'text/csv;charset=utf-8' }), `${slugForFile(props.site.slug || props.site.name)}-transactions-${day}.csv`)
	} catch (err) {
		notify({ group: 'error', text: describeError(err, 'Could not export the transactions.') })
	}
	exporting.value = false
}

// --- rows ------------------------------------------------------------------------
const fmtDateTime = (v) => (v ? new Date(v).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—')
const fmtDate = (v) => {
	if (!v) return '—'
	const d = new Date(v)
	return d.getFullYear() === new Date().getFullYear() ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : d.toLocaleDateString(undefined, { dateStyle: 'medium' })
}
const fmtTime = (v) => (v ? new Date(v).toLocaleTimeString(undefined, { timeStyle: 'short' }) : '')
const viewerLabel = (tx) => tx.viewer?.name || tx.viewer?.email || (tx.viewer?.id ? `Viewer #${tx.viewer.id}` : '—')
const lists = () => ({ content: admin.content.value, categories: admin.categories.value })
const productLabel = (tx) => productTitle(tx, lists())
const itemsLabel = (tx) => {
	const refs = tx.item_refs || []
	if (!refs.length) return tx.product?.scope === 'site' ? 'Whole site' : ''
	const names = refs.slice(0, 2).map((r) => refLabel(r, lists()))
	return names.join(', ') + (refs.length > 2 ? ` +${refs.length - 2}` : '')
}
const amountLabel = (tx) => {
	if (tx.free) return 'Free'
	if (isIncomplete(tx)) return formatMoney(tx.price ?? tx.amount, tx.currency)
	return formatMoney(tx.amount, tx.currency)
}
const refundedLabel = (tx) => (Number(tx.refunded_amount) > 0 ? `−${formatMoney(tx.refunded_amount, tx.currency)}` : '')
const statusLabel = (tx) => (isIncomplete(tx) ? 'Incomplete' : TX_STATUS_LABELS[tx.status] || tx.status)

// --- detail panel ----------------------------------------------------------------
const detailOpen = ref(false)
const detailTx = ref(null)
const openDetail = (tx) => {
	detailTx.value = tx
	detailOpen.value = true
}
watch(detailOpen, (open) => {
	const query = { ...route.query }
	if (open && detailTx.value) query.tx = String(detailTx.value.public_id || detailTx.value.id)
	else delete query.tx
	if (!_.isEqual(query, route.query)) router.replace({ query })
})
// keep the panel in sync when the row is replaced after a refund
watch(rows, (list) => {
	if (!detailTx.value) return
	const fresh = list.find((r) => r.id === detailTx.value.id)
	if (fresh) detailTx.value = fresh
})

// --- refund ----------------------------------------------------------------------
const refundOpen = ref(false)
const refundTx = ref(null)
const refundPayment = ref(null)
const refundReason = ref('')
const refunding = ref(false)
const refundError = ref(null)
const refundAmount = computed(() => (refundTx.value ? paymentAmount(refundTx.value, refundPayment.value) : 0))
const openRefund = (tx) => {
	if (!isRefundable(tx)) return
	refundTx.value = tx
	refundPayment.value = refundTarget(tx)
	refundReason.value = ''
	refundError.value = null
	refundOpen.value = true
}
const confirmRefund = async () => {
	if (!refundTx.value || refunding.value) return
	refunding.value = true
	refundError.value = null
	try {
		// The paywall refunds a payment in full — no amount is sent, castr picks the payment.
		const body = {}
		if (refundPayment.value?.id) body.paymentId = refundPayment.value.id
		if (refundReason.value.trim()) body.reason = refundReason.value.trim().slice(0, 500)
		const res = await SiteAdminService.refundTransaction(props.siteId, refundTx.value.public_id || refundTx.value.id, body)
		if (res?.transaction) rows.value = rows.value.map((r) => (r.id === res.transaction.id ? res.transaction : r))
		if (res?.summary) summary.value = res.summary
		notify({ group: 'success', text: `${formatMoney(res?.refund?.amount ?? refundAmount.value, res?.refund?.currency || refundTx.value.currency)} refunded to ${viewerLabel(refundTx.value)}.` })
		refundOpen.value = false
	} catch (err) {
		refundError.value = describeError(err, 'Could not refund this payment.')
		if (err?.reason === 'nothing_to_refund') load({ refresh: true })
	}
	refunding.value = false
}
</script>

<template>
	<div>
		<div class="mb-4 flex flex-wrap items-center gap-3">
			<div class="min-w-0 flex-1 basis-72">
				<h2 class="mb-0.5 text-headline font-semibold text-surface-text-2">Transactions</h2>
				<p class="mb-0 text-tiny text-surface-7">Every purchase of this site's plans, with refunds. Money is collected by your connected Stripe account; the Paywall is the source of truth.</p>
			</div>
			<div v-if="!paywallUnlinked" class="flex shrink-0 items-center gap-2">
				<FormButton type="secondary" size="md" :disabled="exporting || loading || !!loadError" :is-loading="exporting" data-testid="transactions-export" @click="exportCsv">Export CSV</FormButton>
				<FormButton type="secondary" size="md" :disabled="refreshing || loading" :is-loading="refreshing" @click="load({ refresh: true })">Refresh</FormButton>
			</div>
		</div>

		<Alert v-if="paywallUnlinked" type="normal" class="mb-4" data-testid="transactions-unlinked">
			This site is not linked to your Paywall account yet, so there are no transactions to show. Retry publish from the Overview tab; the list fills in once the link succeeds.
		</Alert>
		<Alert v-else-if="loadError" type="error" class="mb-4" data-testid="transactions-load-error">
			{{ loadErrorText }}
			<span v-if="loadError.reason" class="ml-1 font-mono text-tiny text-surface-8">({{ loadError.reason }})</span>
		</Alert>

		<template v-if="!paywallUnlinked">
			<!-- Filters -->
			<div class="mb-4 flex flex-wrap items-center gap-2 rounded-lg bg-surface-2 p-3" data-testid="transactions-filters">
				<!-- Labels never wrap inside a button; when the row is too narrow the group wraps whole buttons instead (Trello y0qNXs2W). -->
				<div class="flex max-w-full flex-wrap items-center gap-0.5 rounded-md bg-surface-3 p-0.5" role="group" aria-label="Date range" data-testid="transactions-range">
					<button
						v-for="p in RANGE_PRESETS"
						:key="p.id"
						type="button"
						class="h-7 whitespace-nowrap rounded px-2.5 text-tiny font-medium transition-colors"
						:class="rangePreset === p.id ? 'bg-surface-5 text-white' : 'text-surface-8 hover:bg-surface-4 hover:text-white'"
						:aria-pressed="rangePreset === p.id"
						:data-testid="`transactions-range-${p.id}`"
						@click="setPreset(p.id)"
					>
						{{ p.label }}
					</button>
				</div>
				<div v-if="rangePreset === 'custom'" class="flex max-w-full flex-wrap items-center gap-1.5" data-testid="transactions-range-custom">
					<label for="transactions-from" class="sr-only">From</label>
					<FormInput id="transactions-from" v-model="customFrom" type="date" size="sm" class="!w-36" @keydown.enter="applyCustom" />
					<span class="text-tiny text-surface-7">to</span>
					<label for="transactions-to" class="sr-only">To</label>
					<FormInput id="transactions-to" v-model="customTo" type="date" size="sm" class="!w-36" @keydown.enter="applyCustom" />
					<FormButton type="secondary" size="sm" :disabled="!customValid" data-testid="transactions-range-apply" @click="applyCustom">Apply</FormButton>
				</div>
				<label for="transactions-status" class="sr-only">Filter by status</label>
				<FormSelect id="transactions-status" v-model="status" :options="statusOptions" class="!w-40" />
				<div class="relative flex min-w-[12rem] flex-1 items-center sm:max-w-xs">
					<icon-base class="pointer-events-none absolute left-2.5 z-10 text-surface-7" :width="16" :height="16"><icon-search /></icon-base>
					<label for="transactions-search" class="sr-only">Search transactions</label>
					<FormInput id="transactions-search" v-model="q" placeholder="Search viewer, plan or invoice…" class="w-full" custom-classes="!pl-10" />
				</div>
				<span class="ml-auto text-tiny text-surface-7" data-testid="transactions-count">{{ meta.total }} {{ meta.total === 1 ? 'transaction' : 'transactions' }}</span>
			</div>

			<!-- Revenue over the range -->
			<section class="mb-4 rounded-lg bg-surface-2 p-4 sm:p-5">
				<div class="mb-3 flex flex-wrap items-baseline justify-between gap-2">
					<h3 class="mb-0 text-sm font-semibold text-white">Revenue</h3>
					<p class="mb-0 text-tiny text-surface-7" data-testid="transactions-range-label">
						{{ rangeLabel }}
						<template v-if="summary?.truncated"> · newest 2 000 transactions</template>
						<template v-if="summary?.cached_at"> · updated {{ new Date(summary.cached_at).toLocaleTimeString() }}</template>
					</p>
				</div>
				<div v-if="!loadError" class="mb-4">
					<SiteRevenueChart :series="series || []" :purchases="paidPurchaseCount(summary)" :bucket="summary?.bucket || 'day'" :currency="chartCurrency" :height="200" :loading="loading && !summary">
						<template #meta>
							<span v-if="summary?.currency === 'mixed'" class="text-tiny text-surface-7">several currencies — see the cards below</span>
						</template>
						<template #empty>
							<span v-if="series === null && summary" data-testid="revenue-series-missing">Daily totals are not available from the server yet.</span>
							<span v-else>No revenue in this period yet.</span>
						</template>
					</SiteRevenueChart>
				</div>
				<SiteRevenueSummary :summary="summary" :loading="loading && !summary" :content="admin.content.value" :categories="admin.categories.value" />
			</section>

			<Spinner v-if="loading" text="Loading transactions…" classes="text-surface-8 mt-24" spinner-color="var(--c-dark-8)" spinner-size="15px" />

			<EmptyState v-else-if="!rows.length && !loadError" title="No transactions yet" :subtitle="q || status || range.from ? 'Nothing matches these filters.' : 'Purchases show up here as soon as a viewer buys a plan on your site.'" />

			<div v-else-if="rows.length" class="overflow-x-auto rounded-lg bg-surface-2 p-2">
				<table class="w-full min-w-[640px] border-separate border-spacing-y-1 text-left" data-testid="transactions-table">
					<thead>
						<tr class="text-[11px] uppercase tracking-wider text-surface-6">
							<th class="whitespace-nowrap px-2.5 pb-1 font-semibold">Date</th>
							<th class="whitespace-nowrap px-2.5 pb-1 font-semibold">Viewer</th>
							<th class="whitespace-nowrap px-2.5 pb-1 font-semibold">Plan / content</th>
							<th class="whitespace-nowrap px-2.5 pb-1 font-semibold">Amount</th>
							<th class="whitespace-nowrap px-2.5 pb-1 font-semibold">Status</th>
							<th class="whitespace-nowrap px-2.5 pb-1 text-right font-semibold">Actions</th>
						</tr>
					</thead>
					<tbody>
						<tr
							v-for="tx in rows"
							:key="tx.id"
							class="group cursor-pointer"
							:class="isIncomplete(tx) ? 'opacity-75' : ''"
							:data-testid="`transaction-${tx.id}`"
							:data-refundable="isRefundable(tx) ? 'true' : 'false'"
							tabindex="0"
							@click="openDetail(tx)"
							@keydown.enter.self="openDetail(tx)"
						>
							<td class="rounded-l-lg bg-surface-3/60 px-2.5 py-2.5 align-middle transition-colors group-hover:bg-surface-3">
								<span class="block text-nowrap text-sm text-surface-8" :title="fmtDateTime(tx.created_at)">{{ fmtDate(tx.created_at) }}</span>
								<span class="block text-nowrap text-tiny text-surface-7">{{ fmtTime(tx.created_at) }}</span>
							</td>
							<td class="bg-surface-3/60 px-2.5 py-2.5 align-middle transition-colors group-hover:bg-surface-3">
								<span class="block max-w-[180px] truncate text-sm text-surface-text-1" :title="tx.viewer?.email">{{ viewerLabel(tx) }}</span>
								<span v-if="tx.viewer?.name && tx.viewer?.email" class="block max-w-[180px] truncate text-tiny text-surface-7">{{ tx.viewer.email }}</span>
							</td>
							<td class="bg-surface-3/60 px-2.5 py-2.5 align-middle transition-colors group-hover:bg-surface-3">
								<span class="block max-w-[200px] truncate text-sm text-surface-text-1" :title="productLabel(tx)">{{ productLabel(tx) }}<template v-if="tx.recurring ?? tx.product?.recurring"> · {{ periodAdverb(tx.period || tx.product?.period) }}</template></span>
								<span v-if="itemsLabel(tx)" class="block max-w-[200px] truncate text-tiny text-surface-7" :title="itemsLabel(tx)">{{ itemsLabel(tx) }}</span>
								<span v-if="promoCodeOf(tx)" class="mt-0.5 inline-flex items-center gap-1 rounded bg-surface-4 px-1.5 py-0.5 text-[11px] text-surface-8" :title="`Promo code ${promoCodeOf(tx)}`" data-testid="transaction-promo">
									<span class="text-surface-7">Promo</span><code class="text-white">{{ promoCodeOf(tx) }}</code>
								</span>
							</td>
							<td class="bg-surface-3/60 px-2.5 py-2.5 align-middle transition-colors group-hover:bg-surface-3">
								<span class="text-nowrap text-sm font-medium" :class="isIncomplete(tx) ? 'text-surface-6' : 'text-surface-text-1'" :title="isIncomplete(tx) ? 'Checkout not completed — nothing was charged' : ''" data-testid="transaction-amount">{{ amountLabel(tx) }}</span>
								<span v-if="refundedLabel(tx)" class="block text-nowrap text-tiny text-warning" :title="`${formatMoney(tx.refunded_amount, tx.currency)} refunded`" data-testid="transaction-refunded">{{ refundedLabel(tx) }}</span>
							</td>
							<td class="bg-surface-3/60 px-2.5 py-2.5 align-middle transition-colors group-hover:bg-surface-3">
								<StatusPill pill size="sm" :variant="TX_STATUS_VARIANTS[tx.status] || 'muted'">{{ statusLabel(tx) }}</StatusPill>
							</td>
							<td class="rounded-r-lg bg-surface-3/60 px-2.5 py-2.5 text-right align-middle transition-colors group-hover:bg-surface-3">
								<FormButton v-if="isRefundable(tx)" type="secondary" size="sm" title="Refund the latest payment in full" :aria-label="`Refund ${viewerLabel(tx)}`" @click.stop="openRefund(tx)">Refund</FormButton>
								<span v-else class="text-nowrap text-tiny text-surface-6" :title="unrefundableReason(tx)" :aria-label="unrefundableReason(tx)" data-testid="transaction-unrefundable">—</span>
							</td>
						</tr>
					</tbody>
				</table>
			</div>

			<div v-if="meta.total > PER_PAGE" class="mt-4 flex justify-center">
				<FormPagination v-model="page" :total-rows="meta.total" :per-page="PER_PAGE" />
			</div>
		</template>

		<SiteTransactionPanel v-model="detailOpen" :transaction="detailTx" :site-id="siteId" :content="admin.content.value" :categories="admin.categories.value" :refunding="refunding" @refund="openRefund" />

		<!-- Refund -->
		<ModalBox v-model="refundOpen" title="Refund this payment?" size="sm" z-index="z-[60]">
			<div v-if="refundTx" class="flex flex-col gap-3 px-4 pb-4 sm:px-6 sm:pb-6" data-testid="refund-modal">
				<Alert v-if="refundError" type="error" class="!mb-0">{{ refundError }}</Alert>
				<dl class="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 rounded-lg bg-surface-3 px-3 py-2.5 text-sm">
					<dt class="text-surface-7">Viewer</dt><dd class="m-0 truncate text-white">{{ viewerLabel(refundTx) }}</dd>
					<dt class="text-surface-7">Plan</dt><dd class="m-0 truncate text-white">{{ productLabel(refundTx) }}</dd>
					<dt class="text-surface-7">Payment</dt><dd class="m-0 text-white">{{ formatMoney(refundAmount, refundPayment?.currency || refundTx.currency) }}<template v-if="refundPayment?.created_at"> · {{ fmtDateTime(refundPayment.created_at) }}</template></dd>
				</dl>
				<p class="mb-0 text-tiny text-surface-7" data-testid="refund-explainer">
					Refunds are always for the full payment: {{ formatMoney(refundAmount, refundPayment?.currency || refundTx.currency) }} goes back to the viewer's card through Stripe and their access ends. Partial amounts are not possible.
					<template v-if="(refundTx.payments || []).filter((p) => p.status === 'paid').length > 1">Only the latest payment of this subscription is refunded.</template>
				</p>
				<FormInput id="refund-reason" v-model="refundReason" label="Reason (optional, kept in Castr's logs)" placeholder="e.g. Duplicate purchase" input-classes="placeholder:text-surface-6" :maxlength="500" />
			</div>
			<template #footer>
				<footer class="flex flex-col gap-2 border-t border-surface-3 p-4 sm:flex-row-reverse sm:p-6">
					<FormButton type="danger" class="w-full justify-center sm:w-auto" :disabled="refunding" :is-loading="refunding" @click="confirmRefund">
						Refund {{ formatMoney(refundAmount, refundPayment?.currency || refundTx?.currency) }}
					</FormButton>
					<FormButton type="secondary" class="w-full justify-center sm:w-auto" @click="refundOpen = false">Cancel</FormButton>
				</footer>
			</template>
		</ModalBox>
	</div>
</template>
